import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
} from "@mui/material";
import {
  validateNumberInput,
  validatePercentageInput,
} from "../../../../utils/validateFields";

const th = {
  color: "#fff",
  fontWeight: "bold",
  border: "1px solid #ddd",
  whiteSpace: "normal",
  wordBreak: "break-word",
  verticalAlign: "top",
  lineHeight: 1.25,
};
const td = {
  border: "1px solid #ddd",
  verticalAlign: "top",
  wordBreak: "break-word",
  whiteSpace: "normal",
  lineHeight: 1.35,
};
const fyInputSx = {
  "& .MuiInputBase-input": {
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    lineHeight: 1.35,
  },
};

const metricIndicatorLetter = (metricLabel) => {
  const m = metricLabel?.trim().match(/^([a-z])\./i);
  return m ? m[1].toLowerCase() : "";
};

const RelatedTransactionsTable = ({
  formData,
  setFormData,
  data,
  errors,
  setErrors,
  disabled,
  currentFyColumnLabel = "FY ____ (Current Financial Year)",
  previousFyColumnLabel = "FY ____ (Previous Financial Year)",
}) => {
  const handleRowChange = (fieldPath, yearType, value, isPercentage) => {
    const ok = isPercentage
      ? validatePercentageInput(value)
      : validateNumberInput(value);
    if (!ok) return;
    setErrors((prev) => ({ ...prev, [`${fieldPath}.${yearType}`]: "" }));
    setFormData((prev) => {
      const next = { ...prev };
      const paths = fieldPath.split(".");
      let cur = next.relatedPartyTransactions;
      paths.forEach((p, i) => {
        if (i === paths.length - 1) {
          cur[p] = cur[p] || {};
          cur[p][yearType] = value;
        } else cur = cur[p] = cur[p] || {};
      });
      return next;
    });
  };

  const columnLabelForYearKey = (yr) => {
    if (yr === "currentFinancialYear") return currentFyColumnLabel;
    if (yr === "previousFinancialYear") return previousFyColumnLabel;
    return yr.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
  };

  /** One line per (financial year × message), e.g. "A,B — a,c— FY …: This field is Required" */
  const groupedRelatedPartyErrorLines = useMemo(() => {
    const raw = errors && typeof errors === "object" ? errors : {};
    const entries = [];
    Object.keys(raw).forEach((key) => {
      const msg = raw[key];
      if (!msg) return;
      const i = key.lastIndexOf(".");
      entries.push({
        fieldPath: key.slice(0, i),
        yr: key.slice(i + 1),
        msg,
      });
    });

    const byGroup = new Map();
    for (const e of entries) {
      const gk = `${e.yr}\0${e.msg}`;
      if (!byGroup.has(gk)) byGroup.set(gk, []);
      byGroup.get(gk).push(e);
    }

    const lines = [];
    for (const [, items] of byGroup) {
      const { yr, msg } = items[0];
      const paths = new Set(items.map((x) => x.fieldPath));

      const paramsOrdered = [];
      data.forEach((item) => {
        const hit = item.metrics.some((m) => paths.has(m.fieldPath));
        if (hit && !paramsOrdered.includes(item.parameter))
          paramsOrdered.push(item.parameter);
      });

      const lettersOrdered = [];
      data.forEach((item) => {
        item.metrics.forEach((metric) => {
          if (!paths.has(metric.fieldPath)) return;
          const L = metricIndicatorLetter(metric.label);
          if (L && !lettersOrdered.includes(L)) lettersOrdered.push(L);
        });
      });

      const paramsPart =
        paramsOrdered.length > 0
          ? paramsOrdered.join(",")
          : [...paths].join(",");
      const lettersPart =
        lettersOrdered.length > 0 ? lettersOrdered.join(",") : "—";
      const fy = columnLabelForYearKey(yr);
      lines.push(`${paramsPart} — ${lettersPart}— ${fy}: ${msg}`);
    }

    return lines;
  }, [errors, data, currentFyColumnLabel, previousFyColumnLabel]);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          borderRadius: "10px",
          ml: 2,
          width: "98%",
        }}
      >
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow
              sx={{
                borderRadius: "10px",
                backgroundColor: "#1C486B",
                border: "1px solid #ddd",
              }}
            >
              <TableCell sx={{ ...th, width: "12%", maxWidth: "10rem" }}>
                Parameter
              </TableCell>
              <TableCell sx={{ ...th, width: "26%" }}>Metrics</TableCell>
              <TableCell sx={{ ...th, width: "31%", textAlign: "left" }}>
                {currentFyColumnLabel}
              </TableCell>
              <TableCell sx={{ ...th, width: "31%", textAlign: "left" }}>
                {previousFyColumnLabel}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const rpt = formData?.relatedPartyTransactions;
              const val = (path, yk) => {
                const leaf = path
                  .split(".")
                  .reduce(
                    (o, k) =>
                      o != null && o[k] !== undefined ? o[k] : undefined,
                    rpt,
                  );
                const v = leaf?.[yk];
                return v === 0 ? 0 : (v ?? "");
              };
              return (
                <React.Fragment key={index}>
                  {item.metrics.map((metric, idx) => (
                    <TableRow key={idx}>
                      {idx === 0 ? (
                        <TableCell
                          rowSpan={item.metrics.length}
                          sx={{
                            ...td,
                            fontWeight: "bold",
                            width: "12%",
                            maxWidth: "10rem",
                          }}
                        >
                          {item.parameter}
                        </TableCell>
                      ) : null}
                      <TableCell sx={{ ...td, width: "26%" }}>
                        {metric.label}
                      </TableCell>
                      <TableCell sx={{ ...td, width: "31%" }}>
                        <TextField
                          disabled={disabled}
                          value={val(metric.fieldPath, "currentFinancialYear")}
                          placeholder="Enter Number"
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                          sx={fyInputSx}
                          onChange={(e) =>
                            handleRowChange(
                              metric.fieldPath,
                              "currentFinancialYear",
                              e.target.value.replace(/\r?\n/g, " "),
                              metric.isPercentage,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ ...td, width: "31%" }}>
                        <TextField
                          disabled={disabled}
                          value={val(metric.fieldPath, "previousFinancialYear")}
                          placeholder="Enter Number"
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                          sx={fyInputSx}
                          onChange={(e) =>
                            handleRowChange(
                              metric.fieldPath,
                              "previousFinancialYear",
                              e.target.value.replace(/\r?\n/g, " "),
                              metric.isPercentage,
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 1, ml: 2 }}>
        {groupedRelatedPartyErrorLines.map((line) => (
          <p
            key={line}
            style={{
              lineHeight: "1.2",
              margin: "2px 0",
              color: "#d34040",
              fontSize: "10px",
              fontStyle: "italic",
            }}
          >
            {line}
          </p>
        ))}
      </Box>
    </>
  );
};

export default RelatedTransactionsTable;
